/**
 * @fileoverview Category icon registry for timesheets (MOR-906).
 *
 * Component-free by design (fast-refresh safe), mirroring
 * `src/components/settings/tasks/role-icons.ts`: server stores a string
 * key on `timeCategories.icon`, this map turns it into a Lucide component.
 * A stale key renders a plain badge rather than crashing.
 */

import {
  Banknote,
  Briefcase,
  Building2,
  Clock,
  Contact,
  FileText,
  Gavel,
  Mail,
  Percent,
  Phone,
  Scale,
  Search,
  Settings,
  Store,
  Users,
  type LucideIcon,
} from "lucide-react";

export const CATEGORY_ICONS: Record<
  string,
  { Icon: LucideIcon; label: string }
> = {
  settings: { Icon: Settings, label: "Administration" },
  search: { Icon: Search, label: "Investigation" },
  users: { Icon: Users, label: "Creditors" },
  banknote: { Icon: Banknote, label: "Assets" },
  percent: { Icon: Percent, label: "Dividend" },
  "id-card": { Icon: Contact, label: "Employees" },
  store: { Icon: Store, label: "Trading" },
  briefcase: { Icon: Briefcase, label: "Matter work" },
  "file-text": { Icon: FileText, label: "Documents" },
  scale: { Icon: Scale, label: "Legal" },
  gavel: { Icon: Gavel, label: "Court" },
  clock: { Icon: Clock, label: "Time" },
  mail: { Icon: Mail, label: "Correspondence" },
  phone: { Icon: Phone, label: "Calls" },
  building: { Icon: Building2, label: "Company" },
};

export const CATEGORY_ICON_KEYS = Object.keys(CATEGORY_ICONS);

/** Stale/unknown keys → null: render a plain badge, never crash. */
export function categoryIconComponent(
  icon: string | undefined | null,
): LucideIcon | null {
  if (!icon) return null;
  return CATEGORY_ICONS[icon]?.Icon ?? null;
}

/**
 * Tailwind 500-level hex per firm-status colour token, for chart fills
 * where a class can't be used (recharts, inline bar styles).
 */
export const CATEGORY_COLOR_HEX: Record<string, string> = {
  gray: "#6b7280",
  blue: "#3b82f6",
  red: "#ef4444",
  green: "#22c55e",
  emerald: "#10b981",
  amber: "#f59e0b",
  teal: "#14b8a6",
  indigo: "#6366f1",
  purple: "#a855f7",
  pink: "#ec4899",
  orange: "#f97316",
  violet: "#8b5cf6",
  sky: "#0ea5e9",
};

/** Hex for a firm-status colour token; unknown tokens return undefined. */
export function hexForCategoryColor(
  color: string | undefined | null,
): string | undefined {
  if (!color) return undefined;
  return CATEGORY_COLOR_HEX[color];
}
